/**
 * Виртуализированная таблица с оконной подгрузкой с сервера.
 *
 * Полный реестр (9 583 контура) листается целиком: в DOM живёт только видимое
 * окно, а страницы данных приходят по мере прокрутки. Молчаливого усечения нет —
 * общий размер выборки объявлен рядом с таблицей, а незагруженная строка
 * показана скелетом, а не пустотой и не нулём.
 */

import { useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useRef, type ReactNode } from "react";
import { defineMessages } from "../i18n/define";
import { useT } from "../i18n/hooks";
import styles from "./table.module.css";

const m = defineMessages({
  empty: { id: "common.kit.table.empty", defaultMessage: "Ни одной записи не подходит под текущий контекст и фильтры.", description: "Пустое состояние длинной таблицы-реестра: по выбранным фильтрам и дате среза ничего не нашлось" },
});

export interface Column<T> {
  key: string;
  header: ReactNode;
  /** Доля/ширина колонки в grid-template-columns. */
  width: string;
  align?: "left" | "right";
  render: (row: T) => ReactNode;
}

export interface VirtualTableProps<T> {
  columns: ReadonlyArray<Column<T>>;
  /** Полный размер выборки на сервере (не размер загруженного окна). */
  total: number | null;
  /** Загруженные строки по абсолютному индексу. */
  rows: ReadonlyMap<number, T>;
  onRangeChange: (start: number, end: number) => void;
  rowKey: (row: T, index: number) => string | number;
  caption: string;
  meta?: ReactNode;
  footNote?: ReactNode;
  emptyLabel?: string;
  rowHeight?: number;
}

export function VirtualTable<T>({
  columns,
  total,
  rows,
  onRangeChange,
  rowKey,
  caption,
  meta,
  footNote,
  emptyLabel,
  rowHeight = 44,
}: VirtualTableProps<T>) {
  const t = useT();
  const viewportRef = useRef<HTMLDivElement>(null);
  const template = columns.map((column) => column.width).join(" ");

  const virtualizer = useVirtualizer({
    count: total ?? 0,
    getScrollElement: () => viewportRef.current,
    estimateSize: () => rowHeight,
    overscan: 12,
  });

  const virtualRows = virtualizer.getVirtualItems();
  const first = virtualRows[0]?.index;
  const last = virtualRows[virtualRows.length - 1]?.index;

  useEffect(() => {
    if (first === undefined || last === undefined) return;
    onRangeChange(first, last);
  }, [first, last, onRangeChange]);

  return (
    <div className={styles.frame}>
      {meta ? <div className={styles.meta}>{meta}</div> : null}

      <div className={styles.head} style={{ gridTemplateColumns: template }}>
        {columns.map((column) => (
          <div
            key={column.key}
            className={`${styles.cell} ${column.align === "right" ? styles.alignRight : ""}`}
          >
            {column.header}
          </div>
        ))}
      </div>

      <div className={styles.viewport} ref={viewportRef} tabIndex={0}>
        <span className="visually-hidden">{caption}</span>
        {total === 0 ? (
          <div className={styles.empty}>{emptyLabel ?? t(m.empty)}</div>
        ) : (
          <div
            className={styles.canvas}
            style={{ height: `${virtualizer.getTotalSize()}px` }}
          >
            {virtualRows.map((virtualRow) => {
              const row = rows.get(virtualRow.index);
              return (
                <div
                  key={row ? rowKey(row, virtualRow.index) : `skeleton-${virtualRow.index}`}
                  className={`${styles.row} ${row ? styles.rowInteractive : ""}`}
                  style={{
                    gridTemplateColumns: template,
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  {columns.map((column) => (
                    <div
                      key={column.key}
                      className={`${styles.cell} ${column.align === "right" ? styles.alignRight : ""}`}
                    >
                      {row ? (
                        column.render(row)
                      ) : (
                        <div className={styles.skeleton} aria-hidden />
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {footNote ? <div className={styles.footNote}>{footNote}</div> : null}
    </div>
  );
}

export const tableStyles = styles;
